import { useEffect, useMemo, useRef, useState } from 'react'
import {
	fetchUsers,
	fetchMessages,
	sendMessage,
	markMessageAsRead,
} from '../../../api/messages'
import { useIsMobileLg } from 'shared/hooks/useBreakpoint'
import { ensureStudentsLoaded } from 'pages/Students/model/ensureStudentsLoaded'
import { studentsStore } from 'pages/Students/model/studentsStore'
import { prioritySummaryStore } from 'shared/stores/prioritySummaryStore'
import {
	syncMessagesThreadOpened,
	wasMessagesStudentSynced,
} from 'shared/utils/counselorSeenSync'

export const useMessagesPage = () => {
	const isMobileLg = useIsMobileLg()
	const [messageUsers, setMessageUsers] = useState([])
	const [storeStudents, setStoreStudents] = useState([])
	const [selectedUser, setSelectedUser] = useState(null)
	const [messages, setMessages] = useState([])
	const [newMessage, setNewMessage] = useState('')
	const [activeBlock, setActiveBlock] = useState('list')
	const [isStudentInfoCollapsed, setIsStudentInfoCollapsed] = useState(false)
	const [authError, setAuthError] = useState(null)
	const [isLoadingUsers, setIsLoadingUsers] = useState(true)
	const [isLoadingMessages, setIsLoadingMessages] = useState(false)
	const intervalRef = useRef()
	// Failed/unsent messages per student so they survive poll and switch-back
	const failedByStudentRef = useRef({})

	// Load: 1) students with messages (API), 2) all counselor students (store)
	// On 401 (expired token) we don't crash: set empty message list, keep store students if any
	useEffect(() => {
		let cancelled = false
		setIsLoadingUsers(true)
		const load = async () => {
			try {
				const [fromApi, fromStore] = await Promise.all([
					fetchUsers(),
					ensureStudentsLoaded(),
				])
				if (cancelled) return
				setMessageUsers(fromApi || [])
				setStoreStudents(fromStore || studentsStore.students || [])
				if ((fromApi || []).length) setSelectedUser(fromApi[0])
			} catch (err) {
				if (cancelled) return
				const is401 = err?.response?.status === 401
				setMessageUsers([])
				setStoreStudents(studentsStore.students || [])
				if (is401) {
					setAuthError('session_expired')
				}
			} finally {
				if (!cancelled) setIsLoadingUsers(false)
			}
		}
		load()
		return () => { cancelled = true }
	}, [])

	// Merge: first students with conversations (sorted by last message), then rest from store (by id)
	const users = useMemo(() => {
		const withMessages = messageUsers || []
		const withIds = new Set(withMessages.map(u => String(u.id)))
		const withoutMessages = (storeStudents || []).filter(
			s => !withIds.has(String(s.id)),
		).map(s => ({
			id: String(s.id),
			studentId: String(s.id),
			name: s.name || 'Unknown',
			email: s.email || '',
			grade: s.grade || '',
			photoUrl: s.photoUrl || '',
			avatarKey: s.avatarKey || null,
			avatarSource: s.avatarSource || null,
			lastMessage: '',
			lastActiveAt: null,
			unreadCount: 0,
			hasConversation: false,
		}))
		const studentById = new Map(
			(storeStudents || []).map(s => [String(s.id), s]),
		)
		return [...withMessages, ...withoutMessages].map(user => {
			const student = studentById.get(String(user.studentId || user.id))
			return {
				...user,
				grade: user.grade || student?.grade || '',
				photoUrl: user.photoUrl || student?.photoUrl || '',
				avatarKey: user.avatarKey || student?.avatarKey || null,
				avatarSource: user.avatarSource || student?.avatarSource || null,
			}
		})
	}, [messageUsers, storeStudents])

	useEffect(() => {
		let cancelled = false

		const loadMessages = async (isInitial = false) => {
			if (!selectedUser || cancelled) return
			const studentId = selectedUser.studentId || selectedUser.id
			try {
				if (isInitial) setIsLoadingMessages(true)
				const data = await fetchMessages(studentId)
				if (cancelled) return
				const local = failedByStudentRef.current[studentId] || []
				setMessages([...(data || []), ...local])
			} catch (_) {
				// Keep previous messages on fetch error
			} finally {
				if (!cancelled && isInitial) {
					setIsLoadingMessages(false)
				}
			}
		}

		// On user change: clear current thread and show loader
		setMessages([])
		if (selectedUser) {
			loadMessages(true)
		} else {
			setIsLoadingMessages(false)
		}

		// Poll every 10s without resetting loader
		clearInterval(intervalRef.current)
		intervalRef.current = setInterval(() => loadMessages(false), 10000)

		return () => {
			cancelled = true
			clearInterval(intervalRef.current)
		}
	}, [selectedUser])

	const handleSend = async () => {
		const text = newMessage.trim()
		if (!text) return

		const tempId = `temp_${Date.now()}`
		const messageObj = {
			id: tempId,
			from: 'admin',
			text,
			timestamp: new Date().toISOString(),
			isRead: true,
			sendError: false,
		}

		setMessages(prev => [...prev, messageObj])
		setNewMessage('')

		const studentId = selectedUser.studentId || selectedUser.id
		const result = await sendMessage(selectedUser.id, text)
		if (!result.success) {
			const errorText =
				result.error?.response?.data?.error ||
				result.error?.message ||
				'Failed to send message.'
			const failedMsg = {
				id: tempId,
				from: 'admin',
				text,
				timestamp: messageObj.timestamp,
				isRead: true,
				sendError: true,
				sendErrorText: errorText,
			}
			if (!failedByStudentRef.current[studentId]) {
				failedByStudentRef.current[studentId] = []
			}
			failedByStudentRef.current[studentId].push(failedMsg)
			setMessages(prev =>
				prev.map(m =>
					m.id === tempId ? { ...m, sendError: true, sendErrorText: errorText } : m,
				),
			)
			setNewMessage(text)
			return
		}

		// Clear failed state for this student so next merge doesn't re-show old failed
		failedByStudentRef.current[studentId] = []

		// Update list and thread immediately after successful send
		try {
			const [freshUsers, freshMessages] = await Promise.all([
				fetchUsers(),
				fetchMessages(studentId),
			])
			setMessageUsers(freshUsers || [])
			setMessages(freshMessages || [])
		} catch (e) {
			// Non-blocking: message already in thread (optimistic), list will update on next poll
			console.error('Failed to refresh after send:', e)
		}
	}

	const markAsRead = async messageIds => {
		// Locally mark messages in the open thread as read
		setMessages(prev =>
			prev.map(msg =>
				messageIds.includes(msg.id) ? { ...msg, isRead: true } : msg,
			),
		)

		// Optimistically clear unread counter for the currently selected user
		if (selectedUser) {
			const selectedId = String(selectedUser.id)
			const prevUnread =
				messageUsers.find(user => String(user.id) === selectedId)?.unreadCount ||
				0
			setMessageUsers(prev =>
				prev.map(user =>
					String(user.id) === selectedId
						? { ...user, unreadCount: 0 }
						: user,
				),
			)
			if (prevUnread > 0 && !wasMessagesStudentSynced(selectedId)) {
				prioritySummaryStore.optimisticMessagesDecrement(prevUnread)
			}
		}

		try {
			await Promise.all(messageIds.map(id => markMessageAsRead(id)))
		} catch (e) {
			// API error is non-blocking for UI; badge уже убран на фронте
			// eslint-disable-next-line no-console
			console.error('Failed to mark messages as read on server:', e)
		}
	}

	const checkUserHandle = user => {
		const studentId = String(user?.id || user?.studentId || '').trim()
		syncMessagesThreadOpened(studentId, user?.unreadCount)
		setSelectedUser(user)
		if (isMobileLg) {
			setActiveBlock('message')
		}
	}

	const onBackToLList = () => {
		if (isMobileLg) {
			setActiveBlock('list')
		}
	}

	const toggleStudentInfo = () => {
		if (isMobileLg) {
			if (activeBlock === 'student') {
				setActiveBlock('message')
			} else {
				setActiveBlock('student')
			}
		} else {
			setIsStudentInfoCollapsed(!isStudentInfoCollapsed)
		}
	}

	return {
		users,
		selectedUser,
		messages,
		newMessage,
		setNewMessage,
		handleSend,
		setSelectedUser,
		activeBlock,
		setActiveBlock,
		checkUserHandle,
		onBackToLList,
		markAsRead,
		isStudentInfoCollapsed,
		toggleStudentInfo,
		authError,
		isLoadingUsers,
		isLoadingMessages,
	}
}
